/**
 * 発注カードの「入荷 → 検品・シリアル → 顧客へ発送」欄 (2026-10-30〜)。
 * 通常カテゴリーの発注カード (DealFulfillmentTab) と TimeWaver の発注カード (④ 入荷・検品 / ⑤ 顧客へ発送) で共用。
 * 設計: docs/04_design/fulfillment_inspection_mockup ② ③ ④ ⑤
 */
import { inspectionProgress } from '@goenhub/shared';
import { ClipboardCheck, Package, Pencil, Truck } from 'lucide-react';
import { useState } from 'react';

import { FulfillmentSteps } from './FulfillmentSteps';
import { PurchaseOrderInspectionDialog } from '../dialogs/PurchaseOrderInspectionDialog';
import { ShipToCustomerDialog } from '../dialogs/ShipToCustomerDialog';

import { Button } from '@/components/ui/button';
import type { PurchaseOrderRecord } from '@/hooks/useSalesOps';
import { fmtYmd } from '@/lib/purchaseOrderStatus';

interface Props {
  dealId: string;
  po: PurchaseOrderRecord;
  isTimeWaver: boolean;
  contactName?: string | null;
  /** 手順の帯を出す (TimeWaver の発注カードは自前の帯があるため出さない)。 */
  showSteps?: boolean;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-sm bg-[var(--bg-elevated)] px-2.5 py-1.5 text-xs">
      <span className="text-[var(--text-muted)] shrink-0 whitespace-nowrap">{label}</span>
      <span className="text-[var(--text-secondary)] tabular-nums text-right">{children}</span>
    </div>
  );
}

export function PurchaseOrderFulfillmentPanel({
  dealId,
  po,
  isTimeWaver,
  contactName,
  showSteps = true,
}: Props) {
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);

  if (po.status === 'cancelled') return null;

  const progress = inspectionProgress(po, isTimeWaver);
  const ins = po.inspection ?? null;
  const countLabel = isTimeWaver ? 'そろった機器' : 'シリアル';
  const countValue = isTimeWaver ? progress.completeUnits : progress.filledSerials;
  const countDone = progress.totalUnits > 0 && progress.completeUnits === progress.totalUnits;

  return (
    <div className="space-y-2.5">
      {showSteps && <FulfillmentSteps status={po.status} />}

      {po.status === 'pending' && (
        <p className="text-[11px] text-[var(--text-muted)]">
          発注確定の後、商品が弊社に届いたらここで入荷を記録します。
        </p>
      )}

      {po.status === 'ordered' && (
        <>
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            onClick={() => setInspectionOpen(true)}
          >
            <Package className="size-3.5" />
            入荷を記録
          </Button>
          <p className="text-[11px] text-[var(--text-muted)]">
            商品が弊社に届いたら押します。顧客へのメールは送られません。
          </p>
        </>
      )}

      {(po.status === 'received' || po.status === 'inspected' || po.status === 'shipped') && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          <Row label="入荷日">{fmtYmd(ins?.arrivedAt)}</Row>
          <Row label="検品">
            {ins?.inspected ? (
              `${fmtYmd(ins.inspectedAt)}${ins.inspectorName ? ` · ${ins.inspectorName}` : ''}`
            ) : (
              <span className="text-[var(--accent-amber)]">未</span>
            )}
          </Row>
          <Row label={countLabel}>
            <span
              className={countDone ? 'text-[var(--accent-green)]' : 'text-[var(--accent-amber)]'}
            >
              {progress.totalUnits === 0 ? 'なし' : `${countValue}/${progress.totalUnits}`}
            </span>
          </Row>
        </div>
      )}

      {po.status === 'received' && (
        <>
          {progress.undecidedLines > 0 && (
            <p className="text-[11px] text-[var(--accent-amber)]">
              種類が決まっていない商品が {progress.undecidedLines} 行あります。
            </p>
          )}
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            onClick={() => setInspectionOpen(true)}
          >
            <ClipboardCheck className="size-3.5" />
            検品・シリアルを入力
          </Button>
        </>
      )}

      {po.status === 'inspected' && (
        <>
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={() => setInspectionOpen(true)}
          >
            <Pencil className="size-3.5" />
            入荷・検品を編集
          </Button>
          <Button variant="primary" size="sm" className="w-full" onClick={() => setShipOpen(true)}>
            <Truck className="size-3.5" />
            顧客へ発送
          </Button>
          <p className="text-[11px] text-[var(--text-muted)]">
            配送業者・追跡番号を入れて発送すると、顧客へ出荷通知が届き、商談が「発送・納品」に進みます。
          </p>
        </>
      )}

      {po.status === 'shipped' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            <Row label="配送業者">{po.shipCarrierName || '-'}</Row>
            <Row label="追跡番号">
              <span className="font-mono">{po.shipTrackingNumber || '-'}</span>
            </Row>
          </div>
          {ins && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setInspectionOpen(true)}
            >
              入荷・検品の記録を見る
            </Button>
          )}
        </>
      )}

      <PurchaseOrderInspectionDialog
        open={inspectionOpen}
        onOpenChange={setInspectionOpen}
        dealId={dealId}
        po={po}
        isTimeWaver={isTimeWaver}
        contactName={contactName}
        onProceedToShip={() => setShipOpen(true)}
      />
      <ShipToCustomerDialog
        open={shipOpen}
        onOpenChange={setShipOpen}
        dealId={dealId}
        po={po}
        isTimeWaver={isTimeWaver}
        contactName={contactName}
      />
    </div>
  );
}
